import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { FontWeightBold, ColorTextSuccess, FontWeightDefault, ColorTextLight, ColorTextCaution } from "@nxt-costco-com/forge-design-tokens";
import { SHIPS_TO_WAREHOUSE_KEY, FOR_PICKUP_KEY, WAREHOUSE_UNAVAILABLE_KEY, OUT_OF_STOCK_KEY, ITEM_NOT_SOLD_KEY, LOW_STOCK_KEY, IN_STOCK_KEY, AT_WAREHOUSE_KEY, READY_KEY, TOMORROW_KEY, WITHIN_KEY, FOUR_HOURS_KEY, OPTIONS_COLORS_AND_SIZES_MAY_VARY_KEY, BUY_IN_WAREHOUSE_KEY } from "../../../StockStatus/constants.js";
dayjs.extend(timezone);
dayjs.extend(utc);
const getStockStatusMap = (msStore) => {
  const stockStatusMap = {
    INSTOCK: {
      color: ColorTextSuccess,
      status: msStore.getKeyValueItem(IN_STOCK_KEY)
    },
    LOWSTOCK: {
      color: ColorTextCaution,
      status: msStore.getKeyValueItem(LOW_STOCK_KEY)
    },
    ITEMNOTSOLD: {
      color: ColorTextLight,
      status: msStore.getKeyValueItem(ITEM_NOT_SOLD_KEY)
    },
    NOSTOCK: {
      color: ColorTextCaution,
      status: msStore.getKeyValueItem(OUT_OF_STOCK_KEY)
    },
    WIONOTENABLED: {
      color: ColorTextLight,
      status: msStore.getKeyValueItem(WAREHOUSE_UNAVAILABLE_KEY)
    }
  };
  return stockStatusMap;
};
const availabilityPicker = (stockStatusMap, itemAvailability, showWHSMolecule) => {
  if (showWHSMolecule === false) {
    return stockStatusMap.WIONOTENABLED;
  }
  if (itemAvailability in stockStatusMap) {
    return stockStatusMap[itemAvailability];
  }
  return stockStatusMap.ITEMNOTSOLD;
};
const getSuffixStatusText = (msStore, buyable, isVariantProduct) => {
  const forPickupText = msStore.getKeyValueItem(FOR_PICKUP_KEY);
  const atWarehouseText = msStore.getKeyValueItem(AT_WAREHOUSE_KEY);
  return !isVariantProduct && buyable ? forPickupText : atWarehouseText;
};
const getSecondaryStatusText = (stockStatusMap, msStore, stockStatus, isVariantProduct, timezone2) => {
  const { buyable, orderPickup, orderCutOff, availability } = stockStatus || {};
  const stockStatusKey = availability && availability in stockStatusMap ? availability : "ITEMNOTSOLD";
  const stockStatusData = availabilityPicker(stockStatusMap, stockStatusKey);
  let status = "";
  const orderPickUpTime = timezone2 ? dayjs.tz(orderPickup, timezone2) : dayjs(orderPickup);
  const orderCutOffTime = timezone2 ? dayjs.tz(orderCutOff, timezone2) : dayjs(orderCutOff);
  const currentTime = dayjs();
  const pickUpID = dayjs(orderPickup).startOf("day").diff(currentTime.startOf("day"), "day");
  const readyText = msStore.getKeyValueItem(READY_KEY);
  const tomorrowText = msStore.getKeyValueItem(TOMORROW_KEY);
  const withinText = msStore.getKeyValueItem(WITHIN_KEY);
  const fourHoursText = msStore.getKeyValueItem(FOUR_HOURS_KEY);
  const optionsColorsAndSizesMayVaryText = msStore.getKeyValueItem(
    OPTIONS_COLORS_AND_SIZES_MAY_VARY_KEY
  );
  const buyInWarehouseText = msStore.getKeyValueItem(BUY_IN_WAREHOUSE_KEY);
  const inStockText = msStore.getKeyValueItem(IN_STOCK_KEY);
  const lowStockText = msStore.getKeyValueItem(LOW_STOCK_KEY);
  const outOfstockText = msStore.getKeyValueItem(OUT_OF_STOCK_KEY);
  const itemNotSoldText = msStore.getKeyValueItem(ITEM_NOT_SOLD_KEY);
  if (!isVariantProduct && buyable) {
    if ((currentTime.isSame(orderPickUpTime) || currentTime.isAfter(orderPickUpTime)) && currentTime.isBefore(orderCutOffTime)) {
      status = `${readyText} ${withinText} ${fourHoursText}`;
    } else if (pickUpID <= 1) {
      status = `${readyText} ${tomorrowText}`;
    } else if (pickUpID > 1) {
      status = `${readyText} ${orderPickUpTime.format("MM/DD/YYYY")}`;
    }
  } else if (isVariantProduct && ((stockStatusData == null ? void 0 : stockStatusData.status) === inStockText || (stockStatusData == null ? void 0 : stockStatusData.status) === lowStockText)) {
    status = optionsColorsAndSizesMayVaryText;
  } else if (!isVariantProduct && !buyable && (stockStatusData == null ? void 0 : stockStatusData.status) !== outOfstockText && (stockStatusData == null ? void 0 : stockStatusData.status) !== itemNotSoldText) {
    status = buyInWarehouseText;
  }
  return status;
};
const getStockStatusProps = (msStore, stockStatus, isShowroomLocation, productFlags, featureFlags, timezone2) => {
  const stockStatusMap = getStockStatusMap(msStore);
  const { isVariantProduct, isShipToWarehouse } = productFlags || {};
  const {
    buyable: isBOPIWBuyable,
    useWarehouseInventoryAvailability: bopiwAvailabilityStatus,
    availability: bopiwOrWioAvailabilityStatus,
    showWHSMolecule
  } = stockStatus || {};
  const statusTextObj = availabilityPicker(
    stockStatusMap,
    bopiwAvailabilityStatus || bopiwOrWioAvailabilityStatus || "",
    showWHSMolecule
  );
  const suffixText = showWHSMolecule ? getSuffixStatusText(msStore, !!isBOPIWBuyable, !!isVariantProduct) : "";
  const secondaryText = getSecondaryStatusText(
    stockStatusMap,
    msStore,
    stockStatus,
    !!isVariantProduct,
    timezone2
  );
  if (!isBOPIWBuyable && showWHSMolecule && !isShowroomLocation && ((featureFlags == null ? void 0 : featureFlags.bopiwOrStw_feature_on) ?? true) && isShipToWarehouse) {
    const shipToWarehouseText = msStore.getKeyValueItem(
      SHIPS_TO_WAREHOUSE_KEY
    );
    const forPickupText = msStore.getKeyValueItem(FOR_PICKUP_KEY);
    return {
      statusText: shipToWarehouseText,
      statusTextSx: {
        color: ColorTextSuccess,
        fontWeight: FontWeightBold
      },
      suffixText: forPickupText,
      textBlocks: [
        {
          suffixText,
          text: statusTextObj.status,
          textSx: {
            color: statusTextObj.color,
            fontWeight: FontWeightBold
          },
          textVariant: "t7",
          uniqueId: "secondaryText"
        }
      ],
      uniqueId: `warehousetile_${stockStatus == null ? void 0 : stockStatus.fulfillmentCenter}-stw-stockstatus`
    };
  }
  return {
    statusText: statusTextObj.status,
    statusTextSx: {
      color: statusTextObj.color,
      fontWeight: showWHSMolecule ? FontWeightBold : FontWeightDefault
    },
    suffixText,
    textBlocks: [
      {
        text: secondaryText,
        textVariant: "t7",
        uniqueId: "secondaryText"
      }
    ],
    uniqueId: `warehousetile_${stockStatus == null ? void 0 : stockStatus.fulfillmentCenter}-stockstatus`
  };
};
export {
  availabilityPicker,
  getSecondaryStatusText,
  getStockStatusMap,
  getStockStatusProps,
  getSuffixStatusText
};
